import { newApproval } from "@kontextwerk/web-sdk/core/overlay/approvals"
import { openOverlay } from "@kontextwerk/web-sdk/core/overlay/globalStores"
import { newNotification } from "@kontextwerk/web-sdk/core/overlay/notifications"
import {
  archiveWorkOrdersBatch,
  fetchWorkOrderCommunicationProofPdf,
  updateWorkOrder,
} from "../../../../../../../../../api/kehrblatt/work-orders"
import { buildArchiveWorkOrderApprovalDescription } from "../../../../../../../_shared/_logic/components-work-orders-action-tooltips"
import {
  workOrderArchiveIssueMessage,
  workOrderArchiveProcessed,
} from "../../../../../../../_shared/_logic/batch-results"
import { triggerObjectUrlDownload } from "../../../../../../../_shared/_logic/object-url-download"
import type { WorkingHoursByUser } from "../../../../../../../../../_shared/_logic/form-state"
import WorkOrderReopenOverlay from "../_modules/work-order-reopen-overlay"
import type { WorkOrderRow } from "../../../../../_shared/_logic/work-order-table-model"

export type TableActionRunner = <T>(fn: () => Promise<T> | T) => Promise<T | undefined>

export type RowActionCommandContext = {
  organizationId: string
  row: WorkOrderRow
  properties: Property[]
  customers: Customer[]
  contactPoints: ContactPoint[]
  employees: { id: string; label: string }[]
  scheduleEvents: ScheduleEvent[]
  planningSettings: PlanningSettings | null
  workingHoursByUser: WorkingHoursByUser
  allEmployeeIds: string[]
  scopedCanEdit: boolean
  actionLoading: boolean
  runTableAction: TableActionRunner
  onChanged?: () => Promise<void> | void
}

export const downloadWorkOrderCommunicationProofRow = async ({
  row,
  actionLoading,
  runTableAction,
}: Pick<RowActionCommandContext, "row" | "actionLoading" | "runTableAction">) => {
  if (!row?.id || actionLoading) return

  await runTableAction(async () => {
    try {
      const download = await fetchWorkOrderCommunicationProofPdf(row.id)
      const url = URL.createObjectURL(download.blob)
      triggerObjectUrlDownload({ objectUrl: url, filename: download.filename })
      URL.revokeObjectURL(url)
      newNotification({
        class: "success",
        html: "Nachweis heruntergeladen.",
      })
    } catch (err) {
      console.error("work_order_communication_proof_download_failed", err)
      const message =
        (err as { message?: string })?.message ?? "Nachweis konnte nicht heruntergeladen werden."
      newNotification({ class: "error", html: message })
    }
  })
}

export const archiveWorkOrderRow = async ({
  row,
  actionLoading,
  runTableAction,
  onChanged,
}: Pick<RowActionCommandContext, "row" | "actionLoading" | "runTableAction" | "onChanged">) => {
  if (!row?.id || actionLoading || row.workOrder.archived_at) return

  await newApproval({
    title: "Auftrag archivieren",
    description: buildArchiveWorkOrderApprovalDescription(1),
    approvalLabel: "Ja, archivieren",
    declineLabel: "Abbrechen",
    approvalCallback: async () => {
      await runTableAction(async () => {
        try {
          const response = await archiveWorkOrdersBatch([row.id])

          if (!workOrderArchiveProcessed(response, row.id)) {
            newNotification({
              class: "warning",
              html: workOrderArchiveIssueMessage(
                response,
                row.id,
                "Auftrag wurde nicht archiviert.",
              ),
            })

            return
          }

          newNotification({
            class: "success",
            html: "Auftrag archiviert.",
          })
          await onChanged?.()
        } catch (err) {
          console.error("work_order_archive_failed", err)
          const message =
            (err as { message?: string })?.message ?? "Auftrag konnte nicht archiviert werden."
          newNotification({ class: "error", html: message })
        }
      })
    },
  })
}

export const completeWorkOrderRow = async ({
  row,
  scopedCanEdit,
  actionLoading,
  runTableAction,
  onChanged,
}: Pick<
  RowActionCommandContext,
  "row" | "scopedCanEdit" | "actionLoading" | "runTableAction" | "onChanged"
>) => {
  if (!row?.id || actionLoading || !scopedCanEdit) return

  if (row.workOrder?.status !== "OPEN") {
    newNotification({
      class: "info",
      html: "Nur offene Aufträge können abgeschlossen werden.",
    })

    return
  }

  await newApproval({
    title: "Auftrag abschließen",
    description:
      "Diesen Auftrag als abgeschlossen markieren? Danach kann der Kunde für diesen Auftrag keinen Termin mehr vereinbaren, bestätigen oder ändern. Nutzen Sie das nur, wenn der Auftrag wirklich erledigt ist.",
    approvalLabel: "Ja, abschließen",
    declineLabel: "Abbrechen",
    approvalCallback: async () => {
      await runTableAction(async () => {
        try {
          await updateWorkOrder(row.id, {
            status: "COMPLETED",
          } as WorkOrderUpdatePayload)
          newNotification({
            class: "success",
            html: "Auftrag als abgeschlossen markiert.",
          })
          await onChanged?.()
        } catch (err) {
          console.error("work_order_complete_failed", err)
          const message =
            (err as { message?: string })?.message ?? "Auftrag konnte nicht abgeschlossen werden."
          newNotification({ class: "error", html: message })
        }
      })
    },
  })
}

export const reopenWorkOrderRow = ({
  organizationId,
  row,
  properties,
  customers,
  contactPoints,
  employees,
  scheduleEvents,
  planningSettings,
  workingHoursByUser,
  allEmployeeIds,
  scopedCanEdit,
  actionLoading,
  onChanged,
}: RowActionCommandContext) => {
  if (!row?.id || actionLoading || !scopedCanEdit || !organizationId) return

  openOverlay({
    title: "Auftrag wieder öffnen",
    content: WorkOrderReopenOverlay,
    closeOnParentOpen: true,
    isDark: true,
    properties: {
      organizationId,
      workOrder: row.workOrder,
      properties,
      customers,
      contactPoints,
      employees,
      scheduleEvents,
      planningSettings,
      workingHoursByUser,
      allEmployeeIds,
      onChanged: async () => {
        await onChanged?.()
      },
    },
  })
}
